import { Component, OnInit, OnDestroy, HostBinding, Input, OnChanges, SimpleChanges, ViewChild, ElementRef, Output, EventEmitter, CUSTOM_ELEMENTS_SCHEMA, inject } from '@angular/core';
import { Subject } from 'rxjs';
import { NgControl, ControlValueAccessor } from '@angular/forms';
import { DialogService } from 'projects/bis-shared/src/lib/dialog/dialog.service';
import { LookupType } from 'projects/bis-shared/src/lib/lookup/generic-lookup/lookup-type';
import {
  GenericLookupResult,
  GenericLookup,
} from 'projects/bis-shared/src/lib/lookup/generic-lookup/generic-lookup';
import { AppUrls } from 'projects/bis-shared/src/lib/utility/appurls';
import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { InnerLookupService } from './inner-lookup.service';
import { FocusMonitor } from '@angular/cdk/a11y';
import { GenericEntitySearchType } from 'projects/bis-shared/src/lib/dialog/generic-entity-search/generic-entity-search-type';
import { GenericEntitySearchData } from 'projects/bis-shared/src/lib/dialog/generic-entity-search/generic-entity-search-model';
import { MatFormFieldControl } from '@angular/material/form-field';
import { ErrorStateMatcher } from '@angular/material/core';
import { GenericLookupComponent } from 'projects/bis-shared/src/lib/lookup/generic-lookup/generic-lookup.component';
import { RouterLink } from '@angular/router';
import { MatMenuModule } from '@angular/material/menu';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-lookup-component',
  templateUrl: './inner-lookup.component.html',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: InnerLookupComponent,
      multi: true,
    },
  ],
  imports : [
    RouterLink,
    CommonModule,
    MatMenuModule,
    MatButtonModule,
    MatIconModule,
  ],
})
export class InnerLookupComponent
  implements
    MatFormFieldControl<number>,
    OnInit,
    OnDestroy,
    OnChanges,
    ControlValueAccessor {
  private dialogService = inject(DialogService);
  private lookupService = inject(InnerLookupService);
  private elementRef = inject(ElementRef);
  private focusMonitor = inject(FocusMonitor);
  ngControl = inject(NgControl, { optional: true, self: true });

  static nextId = 0;
  controlType = 'lookup-input';

  @Output() dataChanges = new EventEmitter<GenericLookupResult>();
  @HostBinding() id = `${this.controlType}-${InnerLookupComponent.nextId++}`;
  @HostBinding('attr.aria-describedby') describedBy = '';
  @Input() readonly: boolean;
  @Input() disableLink: boolean;

  @Input() lookupType: LookupType;
  @Input() lookupTypes: LookupType[];
  @Input() queryObject?: object;
  @Input() searchType: GenericEntitySearchType;
  @Input() errorStateMatcher: ErrorStateMatcher;

  @ViewChild('lookupMenuButton', { read: ElementRef }) private lookupMenuButton: ElementRef;

  _placeholder: string;
  _disabled: boolean;
  _focused: boolean;
  _required: boolean;
  _loadedValue: number;
  _value: number;
  errorState = false;

  stateChanges = new Subject<void>();
  formattedValue: string;
  routerLink: string;
  autofilled = false;
  multiTypeLookup = false;

  _skipLoadingValue: boolean;
  _valueToLoad: GenericLookupResult;

  onChange: (value: number) => void;
  onTouched: () => void;

  
 

  constructor() {
    const elementRef = this.elementRef;

    if (this.ngControl !== null) {
      this.ngControl.valueAccessor = this;
    }

    this.focusMonitor.monitor(elementRef.nativeElement, true).subscribe(origin => {
      setTimeout(() => {
        this.focused = !!origin;
      });
    });
  }

  ngOnInit() {
    this.multiTypeLookup = this.lookupTypes != null && this.lookupTypes.length > 0;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.lookupType) {
      this.onChangeInternal();
    }
    this.stateChanges.next();
    this.dirtyCheckValue();
  }

  ngOnDestroy() {
    this.stateChanges.complete();

    this.focusMonitor.stopMonitoring(this.elementRef.nativeElement);
  }

  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(value) {
    this._placeholder = value;

    this.stateChanges.next();
  }

  @Input()
  get value(): number {
    return this._value;
  }

  set value(value: number) {
    if (this._value !== value) {
      this._value = value;

      this.stateChanges.next();

      this.onChangeInternal();

      this.onChange?.(value);
    }
  }

  updateErrorState(): void {
    // Method intentionally left blank
  }

  get empty() {
    return !this.value;
  }

  get shouldLabelFloat() {
    return true;
  }

  set focused(value: boolean) {
    this._focused = value;

    this.stateChanges.next();
  }

  get focused() {
    return this._focused;
  }

  @Input()
  get required() {
    return this._required;
  }
  set required(value: boolean) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  @Input()
  get disabled(): boolean {
    return coerceBooleanProperty(this._disabled);
  }
  set disabled(value: boolean) {
    this._disabled = coerceBooleanProperty(value);
    this.stateChanges.next();
  }

  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }
  onContainerClick(): void {
    this.onTouched?.();
  }

  openLookupClick() {
    if (this.lookupType) {
      this.openLookup();
    } else if (this.searchType) {
      this.openSearch();
    } else {
      throw new Error("Couldn't find lookupType or searchType.");
    }
  }

  openLookup() {
    const parentDialog = document.getElementsByClassName('mat-dialog-container')[0];
    parentDialog?.setAttribute('aria-hidden', 'true');

    const data: GenericLookup = {
      lookupType: this.lookupType,
      queryObject: this.queryObject,
      width: this.dialogService.largeWidth,
    };
    this.onTouched?.();
    this.dirtyCheckValue();
    this.dialogService
      .showCustomDialog(GenericLookupComponent, data)
      .subscribe((lookupResult: GenericLookupResult) => {
        parentDialog?.setAttribute('aria-hidden', 'false');

        if (lookupResult) {
          this._valueToLoad = lookupResult;
          this.value = lookupResult.id;
        }
        if (this.multiTypeLookup) {
          this.lookupMenuButton.nativeElement?.focus();
        }
      });
  }

  openLookupWithType(type: LookupType) {
    this.lookupType = type;
    this.openLookup();
  }

  openSearch() {
    const data: GenericEntitySearchData = {
      searchType: this.searchType,
    };
    this.onTouched?.();
    this.dirtyCheckValue();
    this.dialogService
      .openGenericEntitySearch(data)
      .subscribe((lookupResult: GenericLookupResult) => {
        if (lookupResult) {
          this._valueToLoad = lookupResult;
          this.value = lookupResult.id;
        }
      });
  }

  clear() {
    this.value = null;
    this.dataChanges.emit(new GenericLookupResult());
    this.onTouched?.();
    this.dirtyCheckValue();
  }

  onChangeInternal() {
    if (this.value && (this.lookupType || this.searchType) && this.value !== this._loadedValue) {
      if (this._valueToLoad) {
        this.updateAssociatedData(this._valueToLoad);
        this._valueToLoad = null;
      } else {
        const lookupType = this.lookupType || LookupType[this.searchType];
        this.lookupService.getLookupResult(lookupType, this.value).subscribe(result => {
          this.updateAssociatedData(result);
        });
      }
    } else {
      this._loadedValue = null;
      this.formattedValue = null;
      this.routerLink = null;
      this.stateChanges.next();
    }
  }

  updateAssociatedData(lookupResult: GenericLookupResult, inputLookupType?: LookupType): any {
    const lookupType = inputLookupType || this.lookupType || LookupType[this.searchType];
    if (lookupResult.name) {
      this._loadedValue = lookupResult.id;
      this.formattedValue = `${lookupResult.name}`;
    }

    switch (lookupType) {
      case LookupType.HealthcareProvider:
        this.routerLink = AppUrls.entities.healthcareProvider.details(lookupResult.id);
        break;
      case LookupType.HealthcareProviderSupervisory:
        this.routerLink = AppUrls.entities.healthcareProvider.details(lookupResult.id);
        break;
      case LookupType.Claimant:
        this.routerLink = AppUrls.entities.claimant.details(lookupResult.id);
        break;
      case LookupType.Employer:
        this.routerLink = AppUrls.entities.employer.details(lookupResult.id);
        break;
      case LookupType.Insurer:
        this.routerLink = AppUrls.entities.insurer.details(lookupResult.id);
        break;
      case LookupType.InsurerExternal:
        this.routerLink = AppUrls.entities.insurer.details(lookupResult.id);
        break;
      case LookupType.MedicalSupplier:
        this.routerLink = AppUrls.entities.medicalSupplier.details(lookupResult.id);
        break;
      case LookupType.ThirdPartyAdministrator:
        this.routerLink = AppUrls.entities.thirdPartyAdministrator.details(lookupResult.id);
        break;
      case LookupType.ThirdPartyAdministratorExternal:
        this.routerLink = AppUrls.entities.thirdPartyAdministrator.details(lookupResult.id);
        break;
      case LookupType.ExternalSystemUser:
        this.routerLink = '#';
        break;
      case LookupType.SystemUser:
        //TODO this will be updated with upcoming story NWC-7768
        this.routerLink = '#';
        break;
      default:
        alert('Entity Type not yet supported.');
        break;
    }

    lookupResult.lookupType = lookupType;

    this.stateChanges.next();
    this.dataChanges.emit(lookupResult);
  }

  writeValue(value: any): void {
    this.value = coerceNumberProperty(value);
  }
  registerOnChange(fn: (value: number) => void): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
  setDisabledState?(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }
  dirtyCheckValue(): any {
    if (this.ngControl != null) {
      this.updateErrorState();
    }
  }
}
